import {
    withHeaders,
    instance as axiosInstance,
} from '../core/util/axiosInstance';
import { getDefaultHeaders } from '../core/util/other';
import { MutationFunction, UseMutationResult, useMutation } from 'react-query';
import { AxiosResponse } from 'axios';
import { commonShowAlert } from '../lib';
import { useAlert } from '../core';

interface CreateUserGroupProps {
    name: string;
    description: string;
    group: string[];
    token: string;
}
interface UpdateUserGroupProps {
    name: string;
    id: string;
    description: string;
    group: string[];
    token: string;
}

interface DeleteUserGroupProps {
    id: string;
    token: string;
}

export const createUserGroup: MutationFunction<
    AxiosResponse<any>,
    CreateUserGroupProps
> = async (props: CreateUserGroupProps) => {
    const { name, description, group, token } = props;
    const headers = getDefaultHeaders(token);
    const url = '/metadata/usergroup';

    return axiosInstance().post(
        url,
        {
            name,
            description,
            group,
        },
        withHeaders(headers)
    );
};

export const updateUserGroup: MutationFunction<
    AxiosResponse<any>,
    UpdateUserGroupProps
> = async (props: UpdateUserGroupProps) => {
    const { name, description, group, token, id } = props;
    const headers = getDefaultHeaders(token);
    const url = `/metadata/usergroup/${id}`;

    return axiosInstance().put(
        url,
        {
            name,
            description,
            group,
        },
        withHeaders(headers)
    );
};

export const deleteUserGroup: MutationFunction<
    AxiosResponse<any>,
    DeleteUserGroupProps
> = async (props: DeleteUserGroupProps) => {
    const { token, id } = props;
    const headers = getDefaultHeaders(token);
    const url = `/metadata/usergroup/${id}`;

    return axiosInstance().delete(url, withHeaders(headers));
};

export const useUserGroup = (): UseMutationResult<
    AxiosResponse<any>,
    unknown,
    CreateUserGroupProps
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(createUserGroup, {
        onSuccess: () => {
            commonShowAlert(showAlert, hideAlert, 'success', 'Group Created');
        },
        onError: (error: any) => {
            let errorMessage: string =
                error?.data?.message ?? 'Unable to create user group';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const useUpdateUserGroup = (): UseMutationResult<
    AxiosResponse<any>,
    unknown,
    UpdateUserGroupProps
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(updateUserGroup, {
        onSuccess: () => {
            commonShowAlert(showAlert, hideAlert, 'success', 'Group updated');
        },
        onError: (error: any) => {
            let errorMessage: string =
                error?.data?.message ?? 'Unable to update user group';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const useDeleteUserGroup = (): UseMutationResult<
    AxiosResponse<any>,
    unknown,
    DeleteUserGroupProps
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(deleteUserGroup, {
        onSuccess: () => {
            commonShowAlert(showAlert, hideAlert, 'success', 'Group Deleted');
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to delete user group';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};
